import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import {
  Crown, ChevronDown, ChevronLeft, ChevronRight, ArrowRight, Check, Sparkles,
  Apple, Dumbbell, LineChart, Timer, Trophy,
} from "lucide-react";

const HERO = "https://customer-assets.emergentagent.com/job_gym-coach-premium/artifacts/aoaunnnz_WhatsApp%20Image%202026-04-30%20at%208.56.07%20PM.jpeg";

const TOUR_SLIDES = [
  {
    title: "Comidas saludables",
    subtitle: "30 recetas altas en proteína",
    desc: "Desayunos, almuerzos y cenas fáciles de preparar y deliciosas.",
    image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1200&q=85",
    icon: Apple,
  },
  {
    title: "Snacks fitness",
    subtitle: "15 opciones rápidas",
    desc: "Yogur griego, frutos secos, batidos y más para mantenerte en línea.",
    image: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=1200&q=85",
    icon: Apple,
  },
  {
    title: "Rutinas para mujeres",
    subtitle: "Glúteos, piernas, espalda",
    desc: "Ejercicios con imágenes, series, repeticiones y nivel adaptado.",
    image: "https://images.unsplash.com/photo-1646909876562-9a00dab98e65?w=1200&q=85",
    icon: Dumbbell,
  },
  {
    title: "Rutinas para hombres",
    subtitle: "Fuerza y masa muscular",
    desc: "Pecho, espalda, hombros, brazos y piernas. Plan semanal completo.",
    image: "https://images.unsplash.com/photo-1667890787288-ecaaa4bc6994?w=1200&q=85",
    icon: Dumbbell,
  },
  {
    title: "Seguimiento real",
    subtitle: "Gráfica de progreso semanal",
    desc: "Registra tu peso cada semana y mira tu transformación en tiempo real.",
    image: "https://images.unsplash.com/photo-1572007156177-3dda93566f47?w=1200&q=85",
    icon: LineChart,
  },
  {
    title: "Meta saludable",
    subtitle: "Cálculo personalizado",
    desc: "Metas progresivas y realistas según tu edad, sexo y estatura. Paso a paso.",
    image: "https://images.unsplash.com/photo-1689490688215-31bbbfd4ab03?w=1200&q=85",
    icon: Trophy,
  },
  {
    title: "Ayuno 18 horas",
    subtitle: "+ déficit calórico",
    desc: "Plan claro de ventana de comida y calorías para mujeres y hombres.",
    image: "https://images.unsplash.com/photo-1664988935525-e7e0e5e375e3?w=1200&q=85",
    icon: Timer,
  },
  {
    title: "Ganar masa muscular",
    subtitle: "4 comidas + fuerza",
    desc: "Más proteína, carbohidratos saludables y entrenamiento estructurado.",
    image: "https://images.pexels.com/photos/3888405/pexels-photo-3888405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    icon: Dumbbell,
  },
];

const BENEFITS = [
  "Planes fáciles de seguir",
  "Seguimiento real de tu progreso",
  "Rutinas organizadas por nivel",
  "Alimentación saludable y sabrosa",
  "Diseño elegante y fácil de usar",
  "Acceso desde tu celular",
];

// Sparkle positions around photo (rose dots)
const SPARKLES = [
  { top: "-6px", left: "10%", delay: 0, size: 6 },
  { top: "12%", right: "-8px", delay: 0.6, size: 4 },
  { top: "40%", left: "-10px", delay: 1.2, size: 8 },
  { bottom: "30%", right: "-12px", delay: 0.3, size: 5 },
  { bottom: "10%", left: "-6px", delay: 0.9, size: 7 },
  { bottom: "-8px", right: "20%", delay: 1.5, size: 5 },
  { top: "60%", right: "-6px", delay: 1.8, size: 4 },
  { top: "25%", left: "-4px", delay: 2.1, size: 3 },
];

export default function Welcome() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const interval = setInterval(() => emblaApi.scrollNext(), 5000);
    return () => { clearInterval(interval); emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain overflow-x-hidden">
      {/* HEADER */}
      <header className="relative z-50 px-5 md:px-10 py-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <div className="w-11 h-11 rounded-full border border-[var(--mep-pink)]/60 bg-[rgba(232,61,132,0.08)] flex items-center justify-center text-[var(--mep-pink)]">
            <Crown size={20} strokeWidth={1.6} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm sm:text-base font-medium tracking-tight">MI ENTRENADOR</div>
            <div className="font-display text-sm sm:text-base font-extrabold tracking-[0.18em] text-[var(--mep-pink)]">PERSONAL</div>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <span className="hidden sm:inline text-xs uppercase tracking-[0.22em] text-[var(--mep-text-muted)]">¿Ya pagué?</span>
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="flex items-center gap-2 border border-[var(--mep-pink)] text-white font-semibold text-xs sm:text-sm uppercase tracking-[0.2em] px-4 sm:px-5 py-2.5 rounded-md hover:bg-[var(--mep-pink)]/10 transition"
          >
            Iniciar sesión <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-2 pb-20 md:pt-4 md:pb-24 px-5 md:px-10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-[-10%] w-[70%] h-[80%] bg-[radial-gradient(circle_at_center,rgba(232,61,132,0.18),transparent_60%)]" />
          <div className="absolute bottom-10 left-0 w-[40%] h-[40%] bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.08),transparent_70%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto grid md:grid-cols-12 gap-8 md:gap-10 items-center">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="md:col-span-6 text-center md:text-left"
          >
            <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl">
              <span className="block">TU ENTRENADOR</span>
              <span className="block text-[var(--mep-pink)] text-5xl sm:text-6xl md:text-6xl lg:text-7xl xl:text-8xl">PERSONAL</span>
              <span className="block">EN TU BOLSILLO</span>
            </h1>

            <div className="flex items-center justify-center md:justify-start gap-3 my-6 md:my-7">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[var(--mep-gold)]" />
              <Crown size={20} className="text-[var(--mep-gold)]" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[var(--mep-gold)]" />
            </div>

            <p className="text-base md:text-lg text-[var(--mep-text-muted)] max-w-xl mx-auto md:mx-0 leading-relaxed">
              Transforma tu cuerpo con una <span className="text-white">guía clara</span>, alimentación inteligente y <span className="text-[var(--mep-pink)] font-medium">entrenamiento real</span>.
            </p>

            <a href="#tour" data-testid="scroll-cue" className="mt-8 md:mt-10 inline-flex items-center gap-4 group">
              <motion.span
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="w-11 h-11 rounded-full border border-[var(--mep-pink)]/60 bg-[rgba(232,61,132,0.08)] flex items-center justify-center text-[var(--mep-pink)] group-hover:bg-[rgba(232,61,132,0.16)] transition"
              >
                <ChevronDown size={20} />
              </motion.span>
              <span className="text-left leading-tight">
                <span className="block text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[var(--mep-pink)] font-semibold">Desliza hacia abajo</span>
                <span className="block text-xs sm:text-sm text-[var(--mep-text-muted)] mt-0.5">para descubrir todo lo que incluye</span>
              </span>
            </a>
          </motion.div>

          {/* Right: portrait with rose sparkles */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="md:col-span-6 flex justify-center md:justify-end"
          >
            <div className="relative w-[280px] sm:w-[360px] md:w-full max-w-[460px] lg:max-w-[560px] aspect-[4/5]">
              {/* Outer radial pink atmosphere */}
              <div className="absolute -inset-10 rounded-full bg-[radial-gradient(circle_at_center,rgba(232,61,132,0.35),transparent_60%)] blur-2xl" />
              {/* Pink glow ring */}
              <div className="absolute -inset-2 rounded-[1.7rem] bg-[var(--mep-pink)] opacity-20 blur-2xl" />

              {/* Image frame */}
              <div className="relative w-full h-full rounded-[1.5rem] overflow-hidden ring-1 ring-[var(--mep-pink)]/40 shadow-[0_0_60px_-10px_rgba(232,61,132,0.45)]">
                <img
                  src={HERO}
                  alt="Mi Entrenador Personal"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 22%" }}
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[rgba(232,61,132,0.18)] via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
              </div>

              {/* Pink sparkle dots around frame */}
              {SPARKLES.map((s, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: [0, 1, 0.4, 1], scale: [0.6, 1.2, 0.9, 1.1] }}
                  transition={{ duration: 2.4, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute rounded-full bg-[var(--mep-pink)] shadow-[0_0_12px_2px_rgba(232,61,132,0.9)]"
                  style={{
                    top: s.top, left: s.left, right: s.right, bottom: s.bottom,
                    width: s.size, height: s.size,
                  }}
                />
              ))}
              {/* Subtle sparkle icons */}
              <Sparkles className="absolute -top-3 -left-3 text-[var(--mep-pink)] animate-pulse" size={18} />
              <Sparkles className="absolute -bottom-3 -right-3 text-[var(--mep-pink)] animate-pulse" size={22} style={{ animationDelay: "1s" }} />
            </div>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div className="absolute -bottom-1 inset-x-0 pointer-events-none">
          <svg viewBox="0 0 1440 80" className="w-full h-16 md:h-20" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#E83D84" stopOpacity="1" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.8" />
              </linearGradient>
            </defs>
            <path d="M0,40 C320,80 720,0 1080,40 C1280,60 1380,30 1440,40 L1440,80 L0,80 Z" fill="url(#waveGrad)" opacity="0.95" />
            <path d="M0,52 C320,90 720,10 1080,50 C1280,70 1380,40 1440,50" fill="none" stroke="url(#waveGrad)" strokeWidth="1.2" opacity="0.9" />
          </svg>
        </div>
      </section>

      {/* INTERACTIVE TOUR CAROUSEL */}
      <section id="tour" className="py-20 md:py-28 px-5 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="mep-pill-pink mb-4">Recorrido visual</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tighter mb-10 max-w-3xl">
            Todo lo que vas a tener dentro de la app.
          </h2>

          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {TOUR_SLIDES.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div className="flex-[0_0_85%] md:flex-[0_0_55%] lg:flex-[0_0_45%] min-w-0 pl-4" key={i}>
                      <motion.div
                        initial={{ opacity: 0.5, scale: 0.95 }}
                        animate={{ opacity: selected === i ? 1 : 0.6, scale: selected === i ? 1 : 0.95 }}
                        transition={{ duration: 0.4 }}
                        className="mep-tile h-full"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                          <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-[rgba(10,10,10,0.7)] backdrop-blur border border-[var(--mep-border)] flex items-center justify-center text-[var(--mep-gold)]">
                            <Icon size={18} />
                          </div>
                        </div>
                        <div className="p-6 md:p-8">
                          <div className="text-eyebrow mb-2">{s.subtitle}</div>
                          <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mb-2">{s.title}</h3>
                          <p className="text-[var(--mep-text-muted)] text-sm md:text-base leading-relaxed">{s.desc}</p>
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between mt-8">
              <div className="flex gap-2">
                {TOUR_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => emblaApi?.scrollTo(i)}
                    data-testid={`tour-dot-${i}`}
                    className={`h-1.5 transition-all rounded-full ${selected === i ? "w-10 bg-[var(--mep-pink)]" : "w-3 bg-[var(--mep-border)]"}`}
                  />
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="tour-prev-btn"
                  onClick={() => emblaApi?.scrollPrev()}
                  className="w-11 h-11 rounded-full border border-[var(--mep-border)] hover:border-[var(--mep-gold)] flex items-center justify-center transition"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  data-testid="tour-next-btn"
                  onClick={() => emblaApi?.scrollNext()}
                  className="w-11 h-11 rounded-full border border-[var(--mep-border)] hover:border-[var(--mep-gold)] flex items-center justify-center transition"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-28 px-5 md:px-10 border-t border-[var(--mep-border)]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="mep-pill-gold mb-4">Beneficios</div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tighter mb-6">
              Una app pensada para resultados reales.
            </h2>
            <p className="text-[var(--mep-text-muted)] mb-10 leading-relaxed">
              Sin trucos, sin promesas vacías. Solo planes claros, rutinas eficaces y seguimiento honesto.
            </p>
            <ul className="space-y-4">
              {BENEFITS.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-[rgba(212,175,55,0.12)] border border-[var(--mep-gold)]/40 flex items-center justify-center">
                    <Check size={14} className="text-[var(--mep-gold)]" />
                  </div>
                  <span className="text-base md:text-lg">{b}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[var(--mep-border)]">
            <img
              src="https://customer-assets.emergentagent.com/job_gym-coach-premium/artifacts/9fku5cre_WhatsApp%20Image%202026-04-27%20at%2010.10.23%20AM.jpeg"
              alt="Transformación"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-36 px-5 md:px-10 text-center relative overflow-hidden">
        <div className="max-w-3xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="mep-pill-pink mb-6 mx-auto">El momento es ahora</div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-8">
              Empieza hoy tu <span className="font-accent text-[var(--mep-gold)]">transformación</span>.
            </h2>
            <button
              data-testid="cta-unlock-btn"
              onClick={() => navigate("/pago")}
              className="btn-pink px-10 py-5 text-base md:text-lg font-semibold"
            >
              Desbloquear acceso
            </button>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">
              <span>Pago único</span>
              <span className="hidden sm:block">·</span>
              <span>Sin mensualidades</span>
              <span className="hidden sm:block">·</span>
              <span>Acceso completo de por vida</span>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-[var(--mep-border)] px-5 md:px-10 py-10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs text-[var(--mep-text-muted)] tracking-[0.18em] uppercase">
            © Mi Entrenador Personal · Aviso: contenido educativo, no sustituye consulta médica.
          </p>
        </div>
      </footer>
    </div>
  );
}
